import htmx from "htmx.org";
import { css, html, nothing } from "lit";
import { map } from "lit/directives/map.js";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { stateProperty } from "../../property";
import { Note } from "../../schemas/note";
import type { ParserType } from "../../schemas/parser";
import { Tag, type Tags } from "../../schemas/tag";
import { hostStyles } from "../../styles/host.styles";
import { WmElement } from "../element";

type Details = ParserType<typeof Details>;
const Details = stateProperty(Note.schema);

// TODO: make a single "GridDetails" component that takes in the stuff.
export class WmNoteDetails extends WmElement {
  static readonly TAG_NAME = "wm-note-details";

  static override properties = {
    details: Details,
  };

  static styles = [
    hostStyles,
    css`
      .tags {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        gap: var(--wa-space-2xs);
      }
    `,
  ];

  declare details: Details;

  render() {
    if (this.details == null) {
      return nothing;
    }

    const { id, title, contentHtml, tags } = this.details;
    return html`
      <div style="--min-column-size: 14ch; gap: var(--wa-space-2xs);">
        <div>
          <small style="color: var(--wa-color-text-quiet);">Title</small><br />
          ${title}
        </div>
        <div>
          <small style="color: var(--wa-color-text-quiet);">Content</small><br />
          ${contentHtml ? unsafeHTML(contentHtml) : "N/A"}
        </div>
        <div>
          ${this.#renderTags({ tags })}
        </div>
        <div>
          ${this.#renderActions({ id })}
        </div>
      </div>
    `;
  }

  #renderTags(params: { tags: Tags }) {
    const { tags } = params;
    const renderEmpty = () => html`None`;
    const renderTags = (tags: Tags) => html`
      <div class="tags">
        ${map(tags, (t) => Tag.format(t, { size: "xs" }))}
      </div>
    `;

    return html`
      <div>
        <small style="color: var(--wa-color-text-quiet);">Tags</small><br />
        ${tags.length !== 0 ? renderTags(tags) : renderEmpty()}
      </div>
    `;
  }

  #renderActions(params: { id: string }) {
    const { id } = params;
    return html`
      <div>
        <small style="color: var(--wa-color-text-quiet);">Actions</small><br />
        <div>
          <wa-button size="xs" variant="success" @click=${() => htmx.ajax("GET", `/app/notes/${id}`, { target: "body", push: "true" })}>Edit</wa-button>
          <wm-delete-button size="xs" @click=${(e: PointerEvent) => this.dispatchEvent(new WmDeletionRequest(e, id))}></wm-delete-button>
        </div>
      </div>
    `;
  }
}

customElements.define(WmNoteDetails.TAG_NAME, WmNoteDetails);

declare global {
  interface HTMLElementTagNameMap {
    [WmNoteDetails.TAG_NAME]: WmNoteDetails;
  }
}

// TODO: move to events, or rename to be more specific.
export class WmDeletionRequest extends Event {
  static readonly EVENT_NAME = "wm-deletion-request";

  /**
  The original click event at the source of this one.
  */
  readonly source: PointerEvent;
  /**
  The ID of the object to delete.
  */
  readonly id: string;

  constructor(source: PointerEvent, id: string) {
    super(WmDeletionRequest.EVENT_NAME, {
      bubbles: true,
      cancelable: false,
      composed: true,
    });
    this.source = source;
    this.id = id;
  }
}
